import {Component, Suspense, useMemo, type ErrorInfo, type ReactNode} from "react";
import {useNavigate} from "@tanstack/react-router";
import {
    EntityProfileAsideErrorState,
    EntityProfileAsideLoadingState,
} from "~/components/entityProfile/entityProfileAsideCompactStates";
import {EntityProfileAsidePanel} from "~/components/entityProfile/entityProfileAsidePanel";
import {JourneyMoreActionsButton} from "~/components/journeyProfileActionButtons";
import {NestedAsidePanelScope} from "~/contexts/asidePanelContext";
import {useRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {useJourneyEntityProfileController} from "~/workspaceConfig/hostedPages/journeyProfile/-journeyEntityProfileController";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {buildJourneyProfilePath} from "~/utilities/entityProfileNavigation";
import {
    buildEventContainerProfileNavigateSearch,
    type EventContainerProfileSearch,
} from "~/utilities/eventContainerProfileNavigation";

class JourneyEntityProfileAsideErrorBoundary extends Component<
    {children: ReactNode; onClose: () => void},
    {hasError: boolean}
> {
    state = {hasError: false};

    static getDerivedStateFromError(): {hasError: boolean} {
        return {hasError: true};
    }

    componentDidCatch(_error: Error, _info: ErrorInfo): void {
        // Keep panel usable; reload recovers most transient render failures.
    }

    render() {
        if (this.state.hasError === true) {
            return <EntityProfileAsideErrorState />;
        }
        return this.props.children;
    }
}

function JourneyEntityProfileAsideBody({
    journey,
    profileFrom,
    search,
    onClose,
    onExpand: onExpandOverride,
}: {
    journey: EventContainerWithEvents;
    profileFrom?: string;
    search?: EventContainerProfileSearch;
    onClose: () => void;
    /** When set (e.g. test harness), replaces navigate to the production `/profile` route. */
    onExpand?: () => void;
}) {
    const navigate = useNavigate();
    const {workspace, workspaceCapabilities} = useWorkspace();
    const eventContainerTemplate = useRequiredEventContainerTemplate(journey.templateId);

    const resolvedSearch = useMemo((): EventContainerProfileSearch => {
        if (search !== undefined) {
            return search;
        }
        if (profileFrom !== undefined) {
            return buildEventContainerProfileNavigateSearch(profileFrom);
        }
        return {};
    }, [profileFrom, search]);

    const {controller, tabContext, notesLeaveModal} = useJourneyEntityProfileController({
        journey,
        search: resolvedSearch,
        presentation: "aside",
        onRequestClose: onClose,
    });

    const showArchiveAction = isAmaronWorkspace(workspaceCapabilities) === true;

    const title =
        eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase.trim() !== ""
            ? `${eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase} profile`
            : "Lifecycle profile";

    const onExpand = () => {
        if (onExpandOverride !== undefined) {
            onExpandOverride();
            return;
        }
        onClose();
        void navigate({
            to: buildJourneyProfilePath({
                workspaceId: workspace.id,
                eventContainerId: journey.id,
                eventContainerTemplateId: journey.templateId,
                from: resolvedSearch.from,
            }),
        });
    };

    return (
        <>
            <EntityProfileAsidePanel
                controller={controller}
                tabContext={tabContext}
                title={title}
                onExpand={onExpand}
                onClose={onClose}
                headerTrailing={
                    <JourneyMoreActionsButton
                        showArchive={showArchiveAction}
                        onArchive={() => {
                            controller.onFooterAction("more");
                        }}
                        onDelete={() => {
                            controller.onFooterAction("more");
                        }}
                    />
                }
            />
            {notesLeaveModal}
        </>
    );
}

/**
 * Journey Lifecycle profile aside: controller + Figma shell.
 * Opened from Profile CTA; expand navigates to the full `/profile` page.
 */
export function JourneyEntityProfileAside({
    journey,
    profileFrom,
    search,
    onClose,
    onExpand,
}: {
    journey: EventContainerWithEvents;
    profileFrom?: string;
    search?: EventContainerProfileSearch;
    onClose: () => void;
    /** Override full-profile navigation (config harness → `/w/$workspaceId/$pageKey?journeyId=`). */
    onExpand?: () => void;
}) {
    return (
        // Event row panels opened from the tabs must overlay this aside, not the page aside slot.
        <NestedAsidePanelScope>
            <JourneyEntityProfileAsideErrorBoundary onClose={onClose}>
                <Suspense fallback={<EntityProfileAsideLoadingState />}>
                    <JourneyEntityProfileAsideBody
                        journey={journey}
                        profileFrom={profileFrom}
                        search={search}
                        onClose={onClose}
                        onExpand={onExpand}
                    />
                </Suspense>
            </JourneyEntityProfileAsideErrorBoundary>
        </NestedAsidePanelScope>
    );
}
